import { useMutation } from '@apollo/client'
import { message } from 'antd'
import quotesListQuery from '@Builder/QuotesHomePage/QuotesTabs/query'
import { QUOTE_LIST_SORT_KEY } from '@Builder/QuotesHomePage/logic'
import orderDetailsQuery from '@Orders/OrderDetailsPage/Queries/orderDetailsQuery'
import ordersListQuery from '@Orders/AllOrdersPage/query'
import { teamQuery } from '@Teams/TeamDetailsPage/query'
import { teamOrdersQuery } from '@Teams/TeamDetailsPage/TeamOrdersPanel/teamOrdersQuery'
import { useGetTeamDetailsPageQueryParams } from '@Teams/TeamDetailsPage/logic'
import { DEFAULT_PAGE_LIMIT } from '@Teams/TeamDetailsPage/constants'
import { teamQuotesQuery } from '@Teams/TeamDetailsPage/TeamQuotesPanel/query'

import logger from '~/lib/logger'
import { updateMutationVariablesWithOrderId } from '~/apps/consumer/components/MoveQuoteOrder/logic'
import {
    QuoteDetailsDocument,
    RemoveOrderTeamMembershipMutationVariables,
    useRemoveOrderTeamMembershipMutation,
} from '~/gql/graphql'

import {
    REMOVE_ORDER_FAIL,
    REMOVE_ORDER_SUCCESS,
    REMOVE_QUOTE_FAIL,
    REMOVE_QUOTE_SUCCESS,
} from './constants'
import removeTeamQuoteMutation from './removeTeamQuoteMutation'
import { RemoveQuoteTeamMembership as RemoveQuoteTeamMembershipType } from './__generated__/RemoveQuoteTeamMembership'

export function MutationOperations() {
    const [removeOrder] = useRemoveOrderTeamMembershipMutation()
    const [removeQuote] = useMutation<RemoveQuoteTeamMembershipType>(
        removeTeamQuoteMutation,
    )

    const { query: searchText, page: pageNumber } =
        useGetTeamDetailsPageQueryParams()

    return {
        removeQuote: async (
            quoteId: string,
            teamId: number,
            userId: number,
        ) => {
            try {
                const results = await removeQuote({
                    variables: {
                        quoteId,
                        teamId,
                        userId,
                    },
                    refetchQueries: [
                        {
                            query: teamQuotesQuery,
                            variables: {
                                teamId,
                                userId,
                                filters: { searchText },
                                limit: DEFAULT_PAGE_LIMIT,
                                offset: (+pageNumber - 1) * DEFAULT_PAGE_LIMIT,
                            },
                        },
                        {
                            query: quotesListQuery,
                            variables: {
                                limit: DEFAULT_PAGE_LIMIT,
                                offset: (+pageNumber - 1) * DEFAULT_PAGE_LIMIT,
                                sort: QUOTE_LIST_SORT_KEY,
                            },
                        },
                        {
                            query: teamQuery,
                            variables: { teamId },
                        },
                        {
                            query: QuoteDetailsDocument,
                            variables: {
                                id: quoteId,
                            },
                        },
                    ],
                })

                if (results?.errors?.length && results?.errors?.length > 0) {
                    throw new Error(results.errors[0].message)
                }

                message.success(REMOVE_QUOTE_SUCCESS)
                return true
            } catch (err) {
                message.error(REMOVE_QUOTE_FAIL)
                logger.exception(err, {
                    extra: {
                        info: REMOVE_QUOTE_FAIL,
                        quoteId,
                        teamId,
                    },
                })
                return false
            }
        },
        removeOrder: async (
            orderId: string,
            orderName: string,
            teamId: number,
            userId: number,
        ) => {
            try {
                const variables: RemoveOrderTeamMembershipMutationVariables = {
                    teamId,
                    userId,
                    erpSaleOrderId: null,
                    platformOrderId: null,
                }

                updateMutationVariablesWithOrderId(orderId, variables)

                const results = await removeOrder({
                    variables,
                    refetchQueries: [
                        {
                            query: teamOrdersQuery,
                            variables: {
                                teamId,
                                userId,
                                filters: { searchText },
                                limit: DEFAULT_PAGE_LIMIT,
                                offset: (+pageNumber - 1) * DEFAULT_PAGE_LIMIT,
                            },
                        },
                        {
                            query: teamQuery,
                            variables: { teamId },
                        },
                        {
                            query: ordersListQuery,
                            variables: {
                                limit: DEFAULT_PAGE_LIMIT,
                                offset: (+pageNumber - 1) * DEFAULT_PAGE_LIMIT,
                            },
                        },
                        {
                            query: orderDetailsQuery,
                            variables: {
                                name: orderName,
                            },
                        },
                    ],
                })

                if (results?.errors?.length && results?.errors?.length > 0) {
                    throw new Error(results.errors[0].message)
                }

                message.success(REMOVE_ORDER_SUCCESS)
                return true
            } catch (err) {
                message.error(REMOVE_ORDER_FAIL)
                logger.exception(err, {
                    extra: {
                        info: REMOVE_ORDER_FAIL,
                        orderId,
                        teamId,
                    },
                })
                return false
            }
        },
    }
}
